import React, { useContext, useEffect, useState } from "react";
import {
	Button,
	Col,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Spinner,
	Table,
	UncontrolledDropdown,
	DropdownToggle,
	DropdownMenu,
	DropdownItem,
	FormGroup,
	Form,
} from "reactstrap";
import { ProductContext } from "../../../store";
import { toast } from "react-toastify";
import { GetData, PostData, DeleteData, PatchData } from "../../../services/FetchData";
import DetailSkeleton from "../../../components/Skeleton/DetailSkeleton";

const ROLES = [
	{ value: "member", label: "Member" },
	{ value: "admin", label: "Admin" },
];

const Team = () => {
	const { userData } = useContext(ProductContext);
	const [organizations, setOrganizations] = useState([]);
	const [currentOrgId, setCurrentOrgId] = useState(null);
	const [members, setMembers] = useState([]);
	const [invitations, setInvitations] = useState([]);
	const [loader, setLoader] = useState(true);
	const [inviteModal, setInviteModal] = useState(false);
	const [inviteEmail, setInviteEmail] = useState("");
	const [inviteRole, setInviteRole] = useState("member");
	const [inviteLoader, setInviteLoader] = useState(false);
	const [removeModal, setRemoveModal] = useState(false);
	const [removeTarget, setRemoveTarget] = useState(null);
	const [removeLoader, setRemoveLoader] = useState(false);
	const [roleModal, setRoleModal] = useState(false);
	const [roleTarget, setRoleTarget] = useState(null);
	const [roleValue, setRoleValue] = useState("member");
	const [roleLoader, setRoleLoader] = useState(false);

	const canManage = () => {
		const me = (members || []).find((m) => Number(m.userId) === Number(userData?.userId));
		return me && (me.role === "owner" || me.role === "admin");
	};

	const loadOrgs = () => {
		GetData("ms1", "api/v1/organization")
			.then((res) => {
				if (res && res.status === "success" && res.data && res.data.organizations) {
					const orgs = res.data.organizations;
					setOrganizations(orgs);
					if (orgs.length > 0 && !currentOrgId) setCurrentOrgId(orgs[0].organizationId);
					else if (orgs.length > 0) setCurrentOrgId((id) => id || orgs[0].organizationId);
				}
			})
			.catch((err) => {toast.error("Failed to load organizations"); console.log(err);})
			.finally(() => setLoader(false));
	};

	useEffect(() => {
		loadOrgs();
	}, []);

	useEffect(() => {
		if (!currentOrgId) return;
		setLoader(true);
		GetData("ms1", "api/v1/organization/" + currentOrgId + "/members")
			.then((res) => {
				if (res && res.status === "success" && res.data && res.data.members) {
					setMembers(res.data.members);
				} else {
					setMembers([]);
				}
			})
			.catch(() => {
				toast.error("Failed to load members");
				setMembers([]);
			})
			.finally(() => setLoader(false));

		GetData("ms1", "api/v1/organization/" + currentOrgId + "/invitations")
			.then((res) => {
				if (res && res.status === "success" && res.data && res.data.invitations) {
					setInvitations(res.data.invitations);
				} else {
					setInvitations([]);
				}
			})
			.catch(() => setInvitations([]));
	}, [currentOrgId]);

	const currentOrg = organizations.find((o) => o.organizationId === currentOrgId);

	const handleInvite = () => {
		const email = (inviteEmail || "").trim();
		if (!email) {
			toast.error("Enter an email address");
			return;
		}
		setInviteLoader(true);
		PostData("ms1", "api/v1/organization/invite", { email, organizationId: currentOrgId, role: inviteRole })
			.then((res) => {
				if (res && res.status === "success") {
					toast.success("Invitation sent");
					setInviteModal(false);
					setInviteEmail("");
					setInviteRole("member");
					GetData("ms1", "api/v1/organization/" + currentOrgId + "/invitations").then((r) => {
						if (r && r.status === "success" && r.data && r.data.invitations) setInvitations(r.data.invitations);
					});
				} else {
					toast.error(res?.message || "Failed to send invitation");
				}
			})
			.catch(() => toast.error("Failed to send invitation"))
			.finally(() => setInviteLoader(false));
	};

	const handleRemove = () => {
		if (!removeTarget) return;
		setRemoveLoader(true);
		DeleteData("ms1", "api/v1/organization/" + currentOrgId + "/members/" + removeTarget.userId, {})
			.then((res) => {
				if (res && res.status === "success") {
					toast.success("Member removed");
					setRemoveModal(false);
					setRemoveTarget(null);
					setMembers((prev) => prev.filter((m) => Number(m.userId) !== Number(removeTarget.userId)));
				} else {
					toast.error(res?.message || "Failed to remove member");
				}
			})
			.catch(() => toast.error("Failed to remove member"))
			.finally(() => setRemoveLoader(false));
	};

	const handleUpdateRole = () => {
		if (!roleTarget) return;
		setRoleLoader(true);
		PatchData("ms1", "api/v1/organization/" + currentOrgId + "/members/" + roleTarget.userId, { role: roleValue })
			.then((res) => {
				if (res && res.status === "success") {
					toast.success("Role updated");
					setRoleModal(false);
					setRoleTarget(null);
					setMembers((prev) =>
						prev.map((m) => (Number(m.userId) === Number(roleTarget.userId) ? { ...m, role: roleValue } : m))
					);
				} else {
					toast.error(res?.message || "Failed to update role");
				}
			})
			.catch(() => toast.error("Failed to update role"))
			.finally(() => setRoleLoader(false));
	};

	if (loader && organizations.length === 0) return <DetailSkeleton />;

	return (
		<>
			<div className="contentBlock">
				<Row className="align-items-center mb-3">
					<Col>
						<h5 className="mb-0">Team</h5>
						{currentOrg && <p className="text-muted small mb-0">{currentOrg.name}</p>}
					</Col>
					<Col xs="auto">
						{organizations.length > 1 && (
							<>
								<Label className="me-2">Organization</Label>
								<Input
									type="select"
									value={currentOrgId || ""}
									onChange={(e) => setCurrentOrgId(Number(e.target.value))}
									style={{ width: "auto", display: "inline-block" }}
								>
									{organizations.map((o) => (
										<option key={o.organizationId} value={o.organizationId}>
											{o.name}
										</option>
									))}
								</Input>
							</>
						)}
					</Col>
				</Row>

				{canManage() && (
					<Button color="primary" className="mb-3" onClick={() => setInviteModal(true)}>
						Invite member
					</Button>
				)}

				{loader ? (
					<Spinner />
				) : (
					<>
						<Table responsive>
							<thead>
								<tr>
									<th>Name</th>
									<th>Email</th>
									<th>Role</th>
									{canManage() && <th className="text-end">Actions</th>}
								</tr>
							</thead>
							<tbody>
								{(members || []).map((m) => (
									<tr key={m.userId}>
										<td>
											{(m.firstName || "") + (m.lastName ? " " + m.lastName : "") || "—"}
										</td>
										<td>{m.email || "—"}</td>
										<td>
											{canManage() && m.role !== "owner" && Number(m.userId) !== Number(userData?.userId) ? (
												<UncontrolledDropdown>
													<DropdownToggle caret color="link" size="sm" className="p-0">
														{m.role}
													</DropdownToggle>
													<DropdownMenu>
														{ROLES.map((r) => (
															<DropdownItem
																key={r.value}
																onClick={() => {
																	setRoleTarget(m);
																	setRoleValue(r.value);
																	setRoleModal(true);
																}}
															>
																{r.label}
															</DropdownItem>
														))}
													</DropdownMenu>
												</UncontrolledDropdown>
											) : (
																m.role
															)}
										</td>
										{canManage() && (
											<td className="text-end">
												{m.role !== "owner" && Number(m.userId) !== Number(userData?.userId) && (
													<Button
														color="link"
														size="sm"
														className="text-danger p-0"
														onClick={() => {
															setRemoveTarget(m);
															setRemoveModal(true);
														}}
													>
														Remove
													</Button>
												)}
											</td>
										)}
									</tr>
								))}
							</tbody>
						</Table>
						{invitations && invitations.length > 0 && (
							<div className="mt-3">
								<h6>Pending invitations</h6>
								<ul className="list-unstyled mb-0">
									{invitations.map((inv, i) => (
										<li key={i} className="text-muted small">
											{inv.email} ({inv.role}) — expires{" "}
											{inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : "—"}
										</li>
									))}
								</ul>
							</div>
						)}
					</>
				)}
			</div>

			<Modal isOpen={inviteModal} toggle={() => setInviteModal(false)}>
				<ModalHeader toggle={() => setInviteModal(false)}>Invite member</ModalHeader>
				<ModalBody>
					<Form className="d-block w-100">
						<FormGroup>
							<Label>Email</Label>
							<Input
								type="email"
								value={inviteEmail}
								onChange={(e) => setInviteEmail(e.target.value)}
								placeholder="colleague@example.com"
								className="mb-2"
							/>
						</FormGroup>
						<FormGroup>
							<Label>Role</Label>
							<Input type="select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}>
								{ROLES.map((r) => (
									<option key={r.value} value={r.value}>
										{r.label}
									</option>
								))}
							</Input>
						</FormGroup>
					</Form>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setInviteModal(false)}>
						Cancel
					</Button>
					<Button color="primary" onClick={handleInvite} disabled={inviteLoader}>
						{inviteLoader ? <Spinner size="sm" /> : "Send invitation"}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal isOpen={removeModal} toggle={() => !removeLoader && setRemoveModal(false)}>
				<ModalHeader toggle={() => !removeLoader && setRemoveModal(false)}>Remove member</ModalHeader>
				<ModalBody>
					{removeTarget && (
						<p>
							Remove {removeTarget.firstName} {removeTarget.lastName} ({removeTarget.email}) from the team?
						</p>
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => !removeLoader && setRemoveModal(false)} disabled={removeLoader}>
						Cancel
					</Button>
					<Button color="danger" onClick={handleRemove} disabled={removeLoader}>
						{removeLoader ? <Spinner size="sm" /> : "Remove"}
					</Button>
				</ModalFooter>
			</Modal>

			<Modal isOpen={roleModal} toggle={() => !roleLoader && setRoleModal(false)}>
				<ModalHeader toggle={() => !roleLoader && setRoleModal(false)}>Change role</ModalHeader>
				<ModalBody>
					{roleTarget && (
						<>
							<p className="mb-2">
								{roleTarget.firstName} {roleTarget.lastName} ({roleTarget.email})
							</p>
							<Label>Role</Label>
							<Input type="select" value={roleValue} onChange={(e) => setRoleValue(e.target.value)}>
								{ROLES.map((r) => (
									<option key={r.value} value={r.value}>
										{r.label}
									</option>
								))}
							</Input>
						</>
					)}
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => !roleLoader && setRoleModal(false)} disabled={roleLoader}>
						Cancel
					</Button>
					<Button color="primary" onClick={handleUpdateRole} disabled={roleLoader}>
						{roleLoader ? <Spinner size="sm" /> : "Update"}
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default Team;
